/**
 * Telemetry Metric Types
 *
 * Defines the available telemetry metrics for analysis.
 * This is the core type used across all frontends.
 *
 * Note: Display configuration (labels, colors, formatting) is intentionally
 * NOT included here - that belongs in each frontend's presentation layer.
 */

/**
 * Available telemetry metrics for analysis and visualization
 */
// 'speedDelta' = speed vs the per-sample session median (zone charts only;
// stamped onto zone-sliced points, never present on full-lap telemetry).
// ALL_METRICS feeds isValidMetric ONLY — no metric picker iterates it, so
// speedDelta cannot leak into the lap-comparison selectors (verified
// 2026-07-13; re-verify with a grep before extending this list again).
export type TelemetryMetric =
  | 'speed'
  | 'speedDelta'
  | 'timeDrift'
  | 'coasting'
  | 'lateralG'
  | 'leanAngle'
  | 'acceleration'
  | 'brake'

/**
 * Track-wide gradient/visualization metrics. Excludes 'speedDelta': that
 * metric is zone-chart-only (a delta vs the session median over a zone
 * window), never a track-wide gradient mode.
 */
export type GradientMetric = Exclude<TelemetryMetric, 'speedDelta'>

/**
 * Array of all available metrics (for iteration)
 */
export const ALL_METRICS: TelemetryMetric[] = [
  'speed',
  'speedDelta',
  'timeDrift',
  'coasting',
  'lateralG',
  'leanAngle',
  'acceleration',
  'brake',
]

/**
 * Check if a string is a valid TelemetryMetric
 */
export function isValidMetric(value: string): value is TelemetryMetric {
  return ALL_METRICS.includes(value as TelemetryMetric)
}
